import { formatBytes, calculateTransferSpeed } from '../utils/fileUtils';
import type { FileTransfer } from '../types';

interface TransferProgressProps {
  transfer: FileTransfer;
}

/**
 * Component showing file transfer progress
 */
export function TransferProgress({ transfer }: TransferProgressProps) {
  const { file, progress, bytesTransferred, totalBytes, startTime, status, error } = transfer;

  const getStatusColor = () => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'failed':
      case 'cancelled':
        return 'bg-red-500';
      case 'transferring':
        return 'bg-primary-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      case 'transferring':
        return 'Transferring...';
      case 'connecting':
        return 'Connecting...';
      case 'initiating':
        return 'Starting...';
      default:
        return 'Idle';
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-2xl">📄</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {file.name}
              </p>
              <p className="text-xs text-gray-600">
                {formatBytes(file.size)}
              </p>
            </div>
          </div>
        </div>
        
        <span className={`text-xs px-2 py-1 rounded-full text-white ${getStatusColor()}`}>
          {getStatusText()}
        </span>
      </div>

      {status === 'transferring' && (
        <div className="space-y-2">
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-primary-600 h-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          
          <div className="flex justify-between text-xs text-gray-600">
            <span>{Math.round(progress)}%</span>
            <span>
              {formatBytes(bytesTransferred)} / {formatBytes(totalBytes)}
            </span>
            <span>{calculateTransferSpeed(bytesTransferred, startTime)}</span>
          </div>
        </div>
      )}

      {status === 'completed' && (
        <div className="text-sm text-green-600 flex items-center gap-1">
          <span>✓</span>
          <span>Transfer complete!</span>
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 mt-2">
          Error: {error}
        </div>
      )}
    </div>
  );
}
